import { createContext, useContext, useState, type ReactNode } from "react";
import { X, CheckCircle, AlertCircle, Info } from "lucide-react";

interface Toast {
  id: number;
  message: string;
  type: "success" | "error" | "info" | "warning";
}

interface ToastContextType {
  success: (msg: string, duration?: number) => void;
  error: (msg: string, duration?: number) => void;
  info: (msg: string, duration?: number) => void;
  warning: (msg: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
};

const TYPE_COLORS = {
  success: { accent: "#34C759", bg: "rgba(52,199,89,0.12)" },
  error:   { accent: "#FF3B30", bg: "rgba(255,59,48,0.12)"  },
  warning: { accent: "#FF9500", bg: "rgba(255,149,0,0.12)"  },
  info:    { accent: "#007AFF", bg: "rgba(0,122,255,0.12)"  },
};

export function ToastProvider({
  children,
  theme = "light",
}: {
  children: ReactNode;
  theme?: "light" | "dark";
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (message: string, type: Toast["type"], duration = 3500) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), duration);
    }
  };

  const removeToast = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const getIcon = (type: Toast["type"]) => {
    switch (type) {
      case "success": return <CheckCircle size={16} />;
      case "error":
      case "warning": return <AlertCircle size={16} />;
      default:        return <Info size={16} />;
    }
  };

  return (
    <ToastContext.Provider
      value={{
        success: (msg, d) => addToast(msg, "success", d),
        error:   (msg, d) => addToast(msg, "error",   d),
        info:    (msg, d) => addToast(msg, "info",     d),
        warning: (msg, d) => addToast(msg, "warning",  d),
      }}
    >
      {children}
      <div
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 w-[calc(100%-2rem)] max-w-[360px] pointer-events-none"
        style={{ animation: "none" }}
      >
        <style>{`
          @keyframes toastIn {
            from { transform: translateY(-10px); opacity: 0; }
            to   { transform: translateY(0);     opacity: 1; }
          }
          .toast-item { animation: toastIn 0.22s ease-out; }
        `}</style>
        {toasts.map((toast) => {
          const { accent, bg } = TYPE_COLORS[toast.type];
          const isDark = theme === "dark";
          return (
            <div
              key={toast.id}
              className="toast-item pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl"
              role="status"
              aria-live="polite"
              style={{
                background: isDark ? "rgba(28,28,32,0.96)" : "rgba(255,255,255,0.97)",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.5)" : "0 8px 24px rgba(0,0,0,0.12)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
              }}
            >
              <div
                className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: bg, color: accent }}
              >
                {getIcon(toast.type)}
              </div>
              <span
                className="flex-1 text-[13px] font-medium leading-snug"
                style={{ color: isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)" }}
              >
                {toast.message}
              </span>
              <button
                className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center transition-colors"
                style={{ color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)" }}
                onClick={() => removeToast(toast.id)}
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
